<template>
  <div>
    <div class="page-header-wrapper padding-rb">
      <PageHeader title="Batch Operation tool" />
    </div>
    <div class="table-wrapper">
      <el-table v-loading="tableLoading" :data="tableData" element-loading-text="Loading...">
        <el-table-column label="No." width="80" align="center">
          <template slot-scope="{ $index }">
            {{ (currentPage - 1) * pageSize + $index + 1 }}
          </template>
        </el-table-column>
        <el-table-column prop="fileName" label="File Name" min-width="250" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <a
              v-if="row.downloadUrl"
              :href="row.downloadUrl"
              class="file-download-link"
              :title="row.fileName"
              @click.prevent="downloadSourceFile(row)"
            >{{ row.fileName }}</a>
            <span v-else>{{ row.fileName }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="functionName" label="Function" min-width="170" />
        <el-table-column prop="status" label="Status" width="130" />
        <el-table-column prop="uploadUser" label="Upload User" min-width="140" show-overflow-tooltip />
        <el-table-column prop="uploadTime" label="Upload Time" width="180" />
        <el-table-column label="Progress" min-width="250">
          <template slot-scope="{ row }">
            <el-progress
              :percentage="overallProgressPercent(row)"
              :status="progressBarStatus(row)"
              :stroke-width="14"
              :show-text="true"
            />
            <div class="progress-detail">
              <span class="progress-step">{{ stepProgressText(row) }}</span>
              <span
                v-if="row.progressLabel && !isFailedProgressLabel(row)"
                class="progress-label"
              >{{ row.progressLabel }}</span>
            </div>
            <div class="progress-detail">
              Success {{ displayCount(row.successCount) }}
              · Fail {{ displayCount(row.failCount) }}
              · Total {{ displayCount(row.uploadCount) }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="uploadCount" label="Upload Count" width="120" align="right" />
        <el-table-column prop="successCount" label="Success Count" width="130" align="right" />
        <el-table-column prop="failCount" label="Fail Count" width="100" align="right" />
        <el-table-column label="Result" width="120" align="center">
          <template slot-scope="{ row }">
            <el-button
              type="danger"
              size="mini"
              :disabled="!canExportErrors(row)"
              @click="downloadErrorFile(row)"
            >
              Export
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrap">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 25, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
        />
      </div>
    </div>
  </div>
</template>

<script>
import downloadjs from "downloadjs";

export default {
  name: "BatchOperationTool",
  data() {
    return {
      tableData: [],
      tableLoading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      timer: null,
    };
  },
  created() {
    this.bus.$on("refresh", ({ name }) => {
      if (name === "Batch Operation tool") {
        this.getTableData();
      }
    });
  },
  activated() {
    this.getTableData();
  },
  beforeDestroy() {
    if (this.timer) clearTimeout(this.timer);
  },
  methods: {
    async getTableData() {
      this.tableLoading = true;
      try {
        const { code, data, msg } = await this.axios.get("/se/batchAddJobList", {
          params: {
            page: this.currentPage,
            pageSize: this.pageSize,
          },
        });
        if (code === 200) {
          const payload = data || {};
          this.tableData = payload.list || [];
          this.total = payload.total || 0;
          if (this.currentPage > 1 && this.tableData.length === 0 && this.total > 0) {
            this.currentPage = 1;
            await this.getTableData();
            return;
          }
          this.startAutoRefresh();
        } else {
          this.$message.error(msg || "Load batch operation records failed.");
        }
      } catch (e) {
        this.$message.error("Load batch operation records failed.");
      } finally {
        this.tableLoading = false;
      }
    },
    handleSizeChange(pageSize) {
      this.pageSize = pageSize;
      this.currentPage = 1;
      this.getTableData();
    },
    handleCurrentChange(currentPage) {
      this.currentPage = currentPage;
      this.getTableData();
    },
    startAutoRefresh() {
      if (this.timer) clearTimeout(this.timer);
      const hasRunning = this.tableData.some(
        (item) => item.status === "Inprogress" || item.status === "Pending",
      );
      if (!hasRunning) return;
      this.timer = setTimeout(() => {
        this.getTableData();
      }, 600000);
    },
    displayCount(val) {
      const n = Number(val);
      return Number.isNaN(n) ? 0 : n;
    },
    isFailedProgressLabel(row) {
      if (row.status === "Failed") return true;
      const label = String(row.progressLabel || "");
      return label.includes("__BATCH_JOB_PROGRESS__");
    },
    formatGroupStepProgress(row) {
      const totalChunks = this.displayCount(row.totalChunks);
      const chunkIndex = this.displayCount(row.chunkIndex);
      const step = this.displayCount(row.progressStep);
      const totalSteps = this.displayCount(row.progressTotal);
      const pct = Math.min(100, Math.max(0, Number(row.stepPercent) || 0));

      if (totalChunks > 0) {
        const groupPart = chunkIndex > 0 ? `第${chunkIndex}组/` : "";
        return `${groupPart}共${totalChunks}组 · 第${step}步 · ${pct}%`;
      }
      if (totalSteps <= 0) return "—";
      return `第${step}/${totalSteps}步 · ${pct}%`;
    },
    stepProgressText(row) {
      if (row.status === "Pending") {
        return "—";
      }
      if (row.status === "Complete" || row.status === "Partial") {
        const totalChunks = this.displayCount(row.totalChunks);
        const totalSteps = this.displayCount(row.progressTotal);
        if (totalChunks > 0) {
          return `第${totalChunks}组/共${totalChunks}组 · 第${totalSteps}步 · 100%`;
        }
        return `${totalSteps}/${totalSteps} (100%)`;
      }
      if (row.status === "Failed") {
        return this.formatGroupStepProgress(row);
      }
      return this.formatGroupStepProgress(row);
    },
    overallProgressPercent(row) {
      const totalChunks = this.displayCount(row.totalChunks);
      const totalSteps = this.displayCount(row.progressTotal);
      if (row.status === "Pending" || totalSteps <= 0) return 0;
      if (row.status === "Complete" || row.status === "Partial") {
        return 100;
      }
      if (totalChunks > 0) {
        const chunkIndex = this.displayCount(row.chunkIndex);
        const step = this.displayCount(row.progressStep);
        const pct = Number(row.stepPercent) || 0;
        if (row.status === "Failed") {
          const completed = (chunkIndex - 1) * totalSteps + (step - 1) + pct / 100;
          const total = totalChunks * totalSteps;
          return total > 0 ? Math.min(99, Math.round((completed / total) * 100)) : 0;
        }
        const completed = (chunkIndex - 1) * totalSteps + (step - 1) + pct / 100;
        const total = totalChunks * totalSteps;
        return total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;
      }
      if (row.status === "Failed") {
        const step = this.displayCount(row.progressStep);
        if (step <= 0) return 0;
        const stepPct = Number(row.stepPercent);
        const stepFraction = stepPct < 0 ? 0 : Math.min(100, Math.max(0, stepPct)) / 100;
        return Math.min(99, Math.round(((step - 1 + stepFraction) / totalSteps) * 100));
      }
      const step = this.displayCount(row.progressStep);
      if (step <= 0) return 0;
      const stepPct = Number(row.stepPercent);
      const stepFraction = stepPct < 0 ? 0 : Math.min(100, Math.max(0, stepPct)) / 100;
      return Math.min(100, Math.round(((step - 1 + stepFraction) / totalSteps) * 100));
    },
    progressBarStatus(row) {
      if (row.status === "Failed") return "exception";
      if (row.status === "Complete") return "success";
      if (row.status === "Partial") return "warning";
      return null;
    },
    canExportErrors(row) {
      if (row.status === "Inprogress" || row.status === "Pending") return false;
      const fail = Number(row.failCount);
      if (!Number.isNaN(fail) && fail > 0) return true;
      return row.status === "Partial" || row.status === "Failed";
    },
    async downloadSourceFile(row) {
      if (!row.downloadUrl && !row.canDownload && !row.s3FileName) return;
      const originalName = row.fileName || `Batch_Operate_${row.id}.xlsx`;
      try {
        const res = await this.axios.get("/se/batchAddJobSourceDownload", {
          params: { jobId: row.id },
          responseType: "blob",
        });
        // axios 拦截器在有 Content-Disposition 时返回 { data: { file, fileName } }
        // 无匹配时直接返回 blob
        let file = null;
        if (res && res.data && res.data.file) {
          file = res.data.file;
        } else if (res instanceof Blob) {
          file = res;
        } else if (res && res.data instanceof Blob) {
          file = res.data;
        } else {
          file = res;
        }
        downloadjs(
          file,
          originalName,
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        );
      } catch (e) {
        this.$message.error("Download source file failed.");
      }
    },
    async downloadErrorFile(row) {
      if (!this.canExportErrors(row)) {
        if (row.status === "Inprogress" || row.status === "Pending") {
          this.$message.warning("The file is still in progress and cannot be downloaded.");
        } else {
          this.$message.warning("No failed rows to export.");
        }
        return;
      }
      try {
        const res = await this.axios.get("/se/batchAddJobErrorExport", {
          params: { jobId: row.id },
          responseType: "blob",
        });
        const file = res && res.data && res.data.file ? res.data.file : res.data;
        const fileName =
          (res && res.data && res.data.fileName) || `Batch_Operate_Error_${row.id}.xlsx`;
        downloadjs(
          file,
          fileName,
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        );
      } catch (e) {
        this.$message.error("Export error result failed.");
      }
    },
  },
};
</script>

<style scoped>
.progress-detail {
  margin-top: 4px;
  font-size: 12px;
  color: #606266;
  line-height: 1.4;
}
.progress-step {
  font-weight: 500;
  margin-right: 8px;
}
.progress-label {
  color: #909399;
}
.file-download-link {
  color: #409eff;
  text-decoration: none;
  cursor: pointer;
}
.file-download-link:hover {
  text-decoration: underline;
}
</style>
